import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { apiRequest } from '../services/api';

const KONTROL_ARALIĞI_MS = 15 * 1000; // 15 saniyede bir

function alarmSesiCal() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [
      { frekans: 880,  baslangic: 0,   sure: 0.15 },
      { frekans: 880,  baslangic: 0.2, sure: 0.15 },
      { frekans: 1100, baslangic: 0.4, sure: 0.25 },
    ].forEach(({ frekans, baslangic, sure }) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(frekans, ctx.currentTime + baslangic);
      gain.gain.setValueAtTime(0.3, ctx.currentTime + baslangic);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + baslangic + sure);
      osc.start(ctx.currentTime + baslangic);
      osc.stop(ctx.currentTime + baslangic + sure + 0.05);
    });
  } catch (e) {}
}

// Toast renk/ikon konfigürasyonu tipe göre
const TOAST_CONFIG = {
  bakiye: {
    border: 'border-orange-100',
    bg: 'bg-orange-50',
    icon: '🔔',
    titlePrefix: 'Ödeme Hatırlatıcı:',
    accent: 'bg-orange-500'
  },
  checkout: {
    border: 'border-rose-100',
    bg: 'bg-rose-50',
    icon: '🚪',
    titlePrefix: 'Check-out Gecikmesi:',
    accent: 'bg-rose-500'
  },
  taksit: {
    border: 'border-amber-100',
    bg: 'bg-amber-50',
    icon: '💳',
    titlePrefix: 'Taksit Vadesi:',
    accent: 'bg-amber-500'
  },
  basari: {
    border: 'border-emerald-100',
    bg: 'bg-emerald-50',
    icon: '✨',
    titlePrefix: 'İşlem Başarılı:',
    accent: 'bg-emerald-500'
  }
};

function AlarmToast({ toastlar, onKapat }) {
  if (toastlar.length === 0) return null;
  return createPortal(
    <div className="fixed bottom-6 right-6 z-[999999] flex flex-col gap-4 w-full max-w-[350px] pointer-events-none">
      {toastlar.map(t => {
        const cfg = TOAST_CONFIG[t.tur] || TOAST_CONFIG.bakiye;
        return (
          <div key={t.id}
            className={`pointer-events-auto group relative overflow-hidden bg-white/80 backdrop-blur-xl border-2 ${cfg.border} rounded-[2rem] shadow-2xl p-5 flex items-start gap-4 transition-all duration-300 animate-slide-in-right`}
          >
            <div className={`absolute top-0 left-0 w-1.5 h-full ${cfg.accent}`}></div>
            <div className={`w-12 h-12 rounded-2xl ${cfg.bg} flex items-center justify-center flex-shrink-0 text-xl shadow-inner`}>
                {cfg.icon}
            </div>
            <div className="flex-1 min-w-0 pr-4">
              <p className="text-[11px] font-black text-gray-900 uppercase tracking-tight leading-tight mb-1">
                {cfg.titlePrefix} {t.adSoyad}
              </p>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest leading-none mb-1.5">{t.altMetin}</p>
              {t.aciklama && (
                <div className="bg-gray-50/50 rounded-xl p-2 mb-1.5">
                   <p className="text-[10px] text-gray-600 italic leading-snug">"{t.aciklama}"</p>
                </div>
              )}
              <p className="text-[9px] font-black text-blue-600 uppercase tracking-widest opacity-80">{t.yonlendirme || 'Detaylar için dokunun'}</p>
            </div>
            <button onClick={() => onKapat(t.id)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-300 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all text-lg font-black">✕</button>
          </div>
        );
      })}
      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%) scale(0.9); opacity: 0; }
          to { transform: translateX(0) scale(1); opacity: 1; }
        }
        .animate-slide-in-right { animation: slide-in-right 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
      `}</style>
    </div>,
    document.body
  );
}

const AlarmContext = createContext(null);

const GOSTERILEN_KEY = 'hf_gosterilen_alarmlar';
function getGosterilenMap() {
  try { return new Map(Object.entries(JSON.parse(localStorage.getItem(GOSTERILEN_KEY) || '{}'))); }
  catch { return new Map(); }
}
function saveGosterilenMap(map) {
  try { localStorage.setItem(GOSTERILEN_KEY, JSON.stringify(Object.fromEntries(map))); } catch {}
}

export function AlarmProvider({ children }) {
  const [aktifAlarmlar,    setAktifAlarmlar]    = useState([]);
  const [tetiklenenIdler,  setTetiklenenIdler]  = useState(new Set());
  const [toastlar,         setToastlar]         = useState([]);
  const gosterilenRef = useRef(getGosterilenMap());

  const toastKapat = useCallback((id) => {
    setToastlar(p => p.filter(t => t.id !== id));
  }, []);

  const toastEkle = useCallback((t) => {
    const id = Date.now().toString() + Math.random();
    const yeni = { ...t, id };
    setToastlar(p => [...p, yeni]);
    setTimeout(() => {
      setToastlar(p => p.filter(x => x.id !== id));
    }, t.sure || 10000);
  }, []);

  const toastGoster = useCallback((yeniToastlar) => {
    if (yeniToastlar.length === 0) return;
    alarmSesiCal();
    const idliToastlar = yeniToastlar.map(t => ({ ...t, id: Date.now().toString() + Math.random() }));
    setToastlar(p => [...p, ...idliToastlar]);
    setTimeout(() => {
      setToastlar(p => p.filter(t => !idliToastlar.some(y => y.id === t.id)));
    }, 15000);
  }, []);

  // ── 1. Bakiye alarmları (manuel kurulan) ──────────────────────────────────
  const kontrolBakiyeAlarmlari = useCallback(async () => {
    try {
      const data = await apiRequest('rezervasyonlar/bakiyeler');
      if (!data) return;

      const tetiklenenler = (data || []).filter(b => {
        if (!b.alarmTarihi) return false;
        const iso = b.alarmTarihi;
        let alarmD;
        if (iso.includes('Z') || iso.match(/[+-]\d{2}:\d{2}$/)) {
          alarmD = new Date(iso);
        } else {
          const [datePart, timePart = '00:00'] = iso.split('T');
          const [y, m, d] = datePart.split('-').map(Number);
          const [h, mi]   = timePart.split(':').map(Number);
          alarmD = new Date(y, m - 1, d, h, mi);
        }
        return alarmD <= new Date();
      });

      const yeniAlarmlar = tetiklenenler.filter(b => {
        const onceki = gosterilenRef.current.get(`bakiye_${b.rezervasyonId}`);
        return onceki !== b.alarmTarihi;
      });

      if (yeniAlarmlar.length > 0) {
        const yeniToastlar = yeniAlarmlar.map(b => ({
          id: `bakiye_${b.rezervasyonId}`,
          tur: 'bakiye',
          adSoyad: b.adSoyad,
          altMetin: `Oda ${b.odaNo} · Kalan: ${(b.kalanBakiye || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL`,
          aciklama: b.alarmAciklamasi,
          yonlendirme: 'Bakiyeler sayfasına giderek alarmı kapatabilirsiniz.',
        }));
        toastGoster(yeniToastlar);

        setAktifAlarmlar(p => {
          const mevcutIdler = new Set(p.map(a => a.rezervasyonId));
          const eklenecekler = yeniAlarmlar.filter(b => !mevcutIdler.has(b.rezervasyonId));
          return [...p, ...eklenecekler];
        });
        setTetiklenenIdler(p => {
          const yeni = new Set(p);
          yeniAlarmlar.forEach(b => yeni.add(b.rezervasyonId));
          return yeni;
        });

        yeniAlarmlar.forEach(b => gosterilenRef.current.set(`bakiye_${b.rezervasyonId}`, b.alarmTarihi));
        saveGosterilenMap(gosterilenRef.current);
      }
    } catch (e) {}
  }, [toastGoster]);

  // ── 2. Check-out gecikmesi ────────────────────────────────────────────────
  const kontrolCheckoutGecikmesi = useCallback(async () => {
    try {
      const data = await apiRequest('rezervasyonlar/bakiyeler');
      if (!data) return;

      const bugun = new Date();
      bugun.setHours(0, 0, 0, 0);

      const gecikmisSler = (data || []).filter(b => {
        if (b.checkOutYapildiMi) return false;
        if (b.durum === 'İptal' || b.durum === 'Beklemede') return false;
        if (!b.cikisTarihi) return false;
        const cikis = new Date(b.cikisTarihi);
        cikis.setHours(0, 0, 0, 0);
        return cikis < bugun;
      });

      const yeniGecikler = gecikmisSler.filter(b => {
        const key = `checkout_${b.rezervasyonId}`;
        return !gosterilenRef.current.has(key);
      });

      if (yeniGecikler.length > 0) {
        const yeniToastlar = yeniGecikler.map(b => {
          const cikis = new Date(b.cikisTarihi);
          const fark  = Math.floor((bugun - cikis) / 86400000);
          return {
            id: `checkout_${b.rezervasyonId}`,
            tur: 'checkout',
            adSoyad: b.adSoyad,
            altMetin: `Oda ${b.odaNo} · Check-out ${fark} gün gecikmeli`,
            aciklama: null,
            yonlendirme: 'Bakiyeler sayfasından check-out işlemini tamamlayın.',
          };
        });
        toastGoster(yeniToastlar);

        yeniGecikler.forEach(b => gosterilenRef.current.set(`checkout_${b.rezervasyonId}`, true));
        saveGosterilenMap(gosterilenRef.current);
      }
    } catch (e) {}
  }, [toastGoster]);

  // ── 3. Vadesi geçmiş taksitler ────────────────────────────────────────────
  const kontrolVadesiGecmisTaksitler = useCallback(async () => {
    try {
      const data = await apiRequest('taksit-planlari');
      if (!data) return;

      const bugun = new Date();
      bugun.setHours(0, 0, 0, 0);

      data.forEach(plan => {
        if (!plan.taksitDetaylar) return;
        plan.taksitDetaylar.forEach(detay => {
          if (detay.odendi) return;
          const vade = new Date(detay.vadeTarihi);
          vade.setHours(0, 0, 0, 0);
          if (vade >= bugun) return;

          const key = `taksit_${detay.taksitDetayId}`;
          if (gosterilenRef.current.has(key)) return;

          const fark = Math.floor((bugun - vade) / 86400000);
          const toast = {
            id: key,
            tur: 'taksit',
            adSoyad: plan.adSoyad,
            altMetin: `Oda ${plan.odaNo} · ${detay.tutar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL · ${fark} gün gecikmeli`,
            aciklama: null,
            yonlendirme: 'Taksit Ödemeleri sayfasından işlemi tamamlayın.',
          };
          toastGoster([toast]);
          gosterilenRef.current.set(key, true);
        });
      });
      saveGosterilenMap(gosterilenRef.current);
    } catch (e) {}
  }, [toastGoster]);

  // ── Tüm kontroller ────────────────────────────────────────────────────────
  const tumKontroller = useCallback(async () => {
    await kontrolBakiyeAlarmlari();
    await kontrolCheckoutGecikmesi();
    await kontrolVadesiGecmisTaksitler();
  }, [kontrolBakiyeAlarmlari, kontrolCheckoutGecikmesi, kontrolVadesiGecmisTaksitler]);

  useEffect(() => {
    tumKontroller();
    const interval = setInterval(tumKontroller, KONTROL_ARALIĞI_MS);
    return () => clearInterval(interval);
  }, [tumKontroller]);

  const alarmOkundu = useCallback((rezervasyonId) => {
    setAktifAlarmlar(p => p.filter(a => a.rezervasyonId !== rezervasyonId));
  }, []);

  const alarmTemizle = useCallback((rezervasyonId) => {
    setTetiklenenIdler(p => { const s = new Set(p); s.delete(rezervasyonId); return s; });
    setAktifAlarmlar(p => p.filter(a => a.rezervasyonId !== rezervasyonId));
    gosterilenRef.current.delete(`bakiye_${rezervasyonId}`);
    saveGosterilenMap(gosterilenRef.current);
  }, []);

  const tumunuOkunduYap = useCallback(() => {
    setAktifAlarmlar([]);
  }, []);

  return (
    <AlarmContext.Provider value={{
      aktifAlarmlar,
      tetiklenenIdler,
      alarmSayisi: aktifAlarmlar.length,
      alarmOkundu,
      alarmTemizle,
      tumunuOkunduYap,
      toastEkle,
    }}>
      {children}
      <AlarmToast toastlar={toastlar} onKapat={toastKapat} />
    </AlarmContext.Provider>
  );
}

export function useAlarm() {
  const ctx = useContext(AlarmContext);
  if (!ctx) throw new Error('useAlarm: AlarmProvider içinde kullanılmalı');
  return ctx;
}
